import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import {
  isArchivedAssignment,
  getArchivedAssignmentLabel,
  formatUnassignedDate,
} from '../../utils/assignmentHistory';
import { 
  FaTable, 
  FaChevronDown, 
  FaChevronUp, 
  FaSearch, 
  FaFileExcel, 
  FaSortUp, 
  FaSortDown, 
  FaSort,
  FaSpinner,
  FaFilter,
  FaUsers,
  FaProjectDiagram,
  FaHospital,
  FaUserMd,
  FaSync,
  FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import styles from './SCTrackingTable.module.css';
import * as XLSX from 'xlsx';

const SCTrackingTable = () => {
  const { isDarkMode } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Raw data
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [centers, setCenters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [externalPersonnels, setExternalPersonnels] = useState([]);

  // Table state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('scName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'sc', 'backup'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  /**
   * Fetch all required data from Firebase
   */
  const fetchAllData = useCallback(async (forceRefresh = false) => {
    if (dataLoaded && !forceRefresh) return;
    setIsLoading(true);
    try {
      const [
        employeesSnap,
        projectsSnap,
        centersSnap,
        customersSnap,
        patientsSnap,
        externalSnap
      ] = await Promise.all([
        getDocs(collection(db, 'Employees')),
        getDocs(collection(db, 'Projects')),
        getDocs(collection(db, 'Centers')),
        getDocs(collection(db, 'Customers')),
        getDocs(collection(db, 'Patients')),
        getDocs(collection(db, 'ExternalPersonnels'))
      ]);

      setEmployees(employeesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setProjects(projectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setCenters(centersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setCustomers(customersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setPatients(patientsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setExternalPersonnels(externalSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      setDataLoaded(true);
      toast.success('SC takip verileri başarıyla yüklendi');
    } catch (error) {
      console.error('Error fetching SC tracking data:', error);
      toast.error('SC takip verileri yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, [dataLoaded]);

  // Load data when expanded
  useEffect(() => {
    if (isExpanded && !dataLoaded) {
      fetchAllData();
    }
  }, [isExpanded, dataLoaded, fetchAllData]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchAllData(true);
  };

  // Handle search input change with error handling
  const handleSearchChange = useCallback((e) => {
    try {
      const value = e.target.value || '';
      setSearchTerm(value);
    } catch (error) {
      console.error('Search input error:', error);
      toast.error('Arama sırasında bir hata oluştu');
    }
  }, []);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    setSortField('scName');
    setSortDirection('asc');
  };

  // Lookup maps
  const employeeMap = useMemo(() => {
    const map = new Map();
    employees.forEach(emp => map.set(emp.id, emp));
    return map;
  }, [employees]);

  const projectMap = useMemo(() => {
    const map = new Map();
    projects.forEach(proj => map.set(proj.id, proj));
    return map;
  }, [projects]);

  const centerMap = useMemo(() => {
    const map = new Map();
    centers.forEach(c => map.set(c.id, c));
    return map;
  }, [centers]);

  const customerMap = useMemo(() => {
    const map = new Map();
    customers.forEach(c => map.set(c['Başlık'], c));
    return map;
  }, [customers]);

  // Build patient stats grouped by projectId
  const patientStatsByProject = useMemo(() => {
    const statsMap = new Map(); // key: projectId => stats
    patients.forEach(p => {
      const key = p.projectId;
      if (!key) return;
      if (!statsMap.has(key)) {
        statsMap.set(key, {
          targeted: 0,
          screened: 0,
          screenFail: 0,
          randomized: 0,
          currentActive: 0,
          total: 0
        });
      }
      const stat = statsMap.get(key);
      stat.total++;
      if (p.status === 'screened' || p.status === 'screenFail' || p.status === 'enrolled' || 
          p.status === 'active' || p.status === 'onMedication' || p.status === 'completed' || p.status === 'withdrawn') {
        stat.screened++;
      }
      if (p.status === 'screenFail') {
        stat.screenFail++;
      }
      if (p.status === 'enrolled' || p.status === 'active' || p.status === 'onMedication' || p.status === 'completed') {
        stat.randomized++;
      }
      if (p.status === 'active' || p.status === 'onMedication') {
        stat.currentActive++;
      }
    });
    return statsMap;
  }, [patients]);

  // Build patient stats grouped by projectId AND centerId (for per-center tracking)
  const patientStatsByProjectCenter = useMemo(() => {
    const statsMap = new Map(); // key: `${projectId}_${centerId}` => stats
    patients.forEach(p => {
      const projectId = p.projectId;
      const centerId = p.centerId || 'unknown';
      if (!projectId) return;
      const key = `${projectId}_${centerId}`;
      if (!statsMap.has(key)) {
        statsMap.set(key, {
          targeted: 0,
          screened: 0,
          screenFail: 0,
          randomized: 0,
          currentActive: 0,
          total: 0
        });
      }
      const stat = statsMap.get(key);
      stat.total++;
      if (['screened', 'screenFail', 'enrolled', 'active', 'onMedication', 'completed', 'withdrawn'].includes(p.status)) {
        stat.screened++;
      }
      if (p.status === 'screenFail') stat.screenFail++;
      if (['enrolled', 'active', 'onMedication', 'completed'].includes(p.status)) stat.randomized++;
      if (p.status === 'active' || p.status === 'onMedication') stat.currentActive++;
    });
    return statsMap;
  }, [patients]);

  /**
   * Build rows: one row per employee-project-center assignment
   * This matches the Excel structure where each SC has multiple rows (one per project/center)
   */
  const tableRows = useMemo(() => {
    const rows = [];

    projects.forEach(project => {
      const projectEmployees = project.employees || [];
      
      projectEmployees.forEach(assignment => {
        // Only include SC-related roles
        const role = assignment.role || '';
        const isSC = role === 'Saha Koordinatörü';
        const isBackup = role === 'Backup Personeli';
        const isNurse = role === 'Klinik Araştırma Hemşiresi';
        const isSCSorumlusu = role === 'Saha Koordinatörü Sorumlusu';
        
        if (!isSC && !isBackup && !isNurse && !isSCSorumlusu) return;

        const employee = employeeMap.get(assignment.id);
        if (!employee) return;
        // Include both active and inactive employees for comprehensive tracking
        // Skip only if employee data is completely missing

        const center = assignment.centerId ? centerMap.get(assignment.centerId) : null;
        const customerName = project.customer || '';
        
        // Get FTE/tariff info
        const rate = assignment.rate;
        let fteDisplay = '';
        if (rate) {
          if (rate.type?.toUpperCase() === 'FTE') {
            fteDisplay = rate.value || '';
          } else {
            fteDisplay = `Saatlik-${rate.value || ''}`;
          }
        }
        if (isBackup) {
          fteDisplay = 'Backup';
        }

        // Get patient stats for this project (or project+center)
        const projectPatientStats = patientStatsByProject.get(project.id) || {
          targeted: 0, screened: 0, screenFail: 0, randomized: 0, currentActive: 0, total: 0
        };

        // Delegation date
        let delegationDate = '';
        if (assignment.assignedAt) {
          try {
            const d = assignment.assignedAt.toDate ? assignment.assignedAt.toDate() : new Date(assignment.assignedAt);
            delegationDate = d.toLocaleDateString('tr-TR');
          } catch {
            delegationDate = String(assignment.assignedAt);
          }
        }

        // Find PI (principal investigator) from ExternalPersonnels linked via center
        let piName = '';
        if (center && center['Yetkili']) {
          const pi = externalPersonnels.find(ep => ep.id === center['Yetkili']);
          if (pi) {
            piName = `${pi['Adı'] || ''} ${pi['Soyadı'] || ''}`.trim();
            if (pi['Ünvan']) {
              piName = `${pi['Ünvan']} ${piName}`;
            }
          }
        }

        rows.push({
          scName: `${employee.eName || ''} ${employee.eSurname || ''}`.trim() || 'Bilinmeyen',
          phone: employee.phone || '',
          email: employee.eMail || '',
          serviceFte: fteDisplay || '',
          project: project.titleShort || project.titleLong || '',
          protocolNumber: project.protocolNumber || '',
          indication: project.projectDepartment || '',
          site: center ? (center['Adı'] || '') : (assignment.centerName || ''),
          pi: piName || '',
          delegation: delegationDate || '',
          targetedPatients: projectPatientStats.total || 0,
          screenedPatients: projectPatientStats.screened || 0,
          screenFailure: projectPatientStats.screenFail || 0,
          randomization: projectPatientStats.randomized || 0,
          currentPatients: projectPatientStats.currentActive || 0,
          sponsor: customerName || '',
          role: role || '',
          status: assignment.status || 'active',
          employeeStatus: employee.Active === 1 ? 'active' : 'inactive',
          employeeId: employee.id || '',
          projectId: project.id || '',
          centerId: assignment.centerId || '',
          // Bu merkezden ayrılmış olsa da satır listede kalır, rozetle işaretlenir.
          isArchived: isArchivedAssignment(assignment),
          unassignedAt: assignment.unassignedAt || ''
        });
      });
    });

    return rows;
  }, [projects, employeeMap, centerMap, patientStatsByProject, externalPersonnels]);

  // Filter & sort
  const filteredAndSortedRows = useMemo(() => {
    try {
      let filtered = [...tableRows];

      // Role filter
      if (roleFilter === 'sc') {
        filtered = filtered.filter(r => r.role === 'Saha Koordinatörü');
      } else if (roleFilter === 'backup') {
        filtered = filtered.filter(r => r.role === 'Backup Personeli');
      } else if (roleFilter === 'nurse') {
        filtered = filtered.filter(r => r.role === 'Klinik Araştırma Hemşiresi');
      } else if (roleFilter === 'scSorumlusu') {
        filtered = filtered.filter(r => r.role === 'Saha Koordinatörü Sorumlusu');
      }

      // Status filter
      if (statusFilter === 'active') {
        filtered = filtered.filter(r => r.employeeStatus === 'active');
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter(r => r.employeeStatus === 'inactive');
      }

      // Search filter
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(row => {
          try {
            return (
              (row.scName || '').toLowerCase().includes(search) ||
              (row.project || '').toLowerCase().includes(search) ||
              (row.site || '').toLowerCase().includes(search) ||
              (row.email || '').toLowerCase().includes(search) ||
              (row.pi || '').toLowerCase().includes(search) ||
              (row.sponsor || '').toLowerCase().includes(search) ||
              (row.protocolNumber || '').toLowerCase().includes(search) ||
              (row.indication || '').toLowerCase().includes(search)
            );
          } catch (error) {
            console.error('Search filter error for row:', row, error);
            return false;
          }
        });
      }

      // Sort
      filtered.sort((a, b) => {
        try {
          let aVal = a[sortField];
          let bVal = b[sortField];

          // Handle null/undefined values
          if (aVal == null) aVal = '';
          if (bVal == null) bVal = '';

          if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = String(bVal).toLowerCase();
            return sortDirection === 'asc' ? aVal.localeCompare(bVal, 'tr') : bVal.localeCompare(aVal, 'tr');
          }
          if (typeof aVal === 'number') {
            const numA = Number(aVal) || 0;
            const numB = Number(bVal) || 0;
            return sortDirection === 'asc' ? numA - numB : numB - numA;
          }
          return 0;
        } catch (error) {
          console.error('Sort error:', error, 'Field:', sortField, 'Values:', a[sortField], b[sortField]);
          return 0;
        }
      });

      return filtered;
    } catch (error) {
      console.error('Filter and sort error:', error);
      toast.error('Tablo filtreleme sırasında bir hata oluştu');
      return [];
    }
  }, [tableRows, searchTerm, sortField, sortDirection, roleFilter, statusFilter]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const uniqueSCs = new Set(filteredAndSortedRows.map(r => r.employeeId));
    const uniqueProjects = new Set(filteredAndSortedRows.map(r => r.projectId));
    const uniqueSites = new Set(filteredAndSortedRows.filter(r => r.site).map(r => r.site));
    return {
      totalRows: filteredAndSortedRows.length,
      uniqueSCs: uniqueSCs.size,
      uniqueProjects: uniqueProjects.size,
      uniqueSites: uniqueSites.size,
      totalScreened: filteredAndSortedRows.reduce((sum, r) => sum + (r.screenedPatients || 0), 0),
      totalRandomized: filteredAndSortedRows.reduce((sum, r) => sum + (r.randomization || 0), 0),
      totalActive: filteredAndSortedRows.reduce((sum, r) => sum + (r.currentPatients || 0), 0)
    };
  }, [filteredAndSortedRows]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  // Excel export
  const handleExcelExport = () => {
    const exportData = filteredAndSortedRows.map(row => ({
      'SC': row.scName,
      'Durum': row.employeeStatus === 'active' ? 'Aktif' : 'Pasif',
      'Telefon': row.phone,
      'E-Posta': row.email,
      'Hizmet-FTE': row.serviceFte,
      'Proje': row.project,
      'Protokol': row.protocolNumber,
      'Endikasyon': row.indication,
      'Merkez': row.site,
      'Merkezden Ayrılma': row.isArchived ? formatUnassignedDate(row) : '',
      'PI': row.pi,
      'Delegasyon': row.delegation,
      'Hedeflenen Hasta': row.targetedPatients,
      'Screen Edilen': row.screenedPatients,
      'Screen Failure': row.screenFailure,
      'Randomizasyon': row.randomization,
      'Güncel Hasta': row.currentPatients,
      'Sponsor': row.sponsor
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SC Takip');
    XLSX.writeFile(wb, `SC_Takip_Tablosu_${new Date().toLocaleDateString('tr-TR')}.xlsx`);
    toast.success('Excel dosyası başarıyla indirildi');
  };

  const columns = [
    { key: 'scName', label: 'SC', width: '160px' },
    { key: 'employeeStatus', label: 'Durum', width: '80px' },
    { key: 'phone', label: 'Telefon', width: '120px' },
    { key: 'email', label: 'E-Posta', width: '200px' },
    { key: 'serviceFte', label: 'Hizmet-FTE', width: '100px' },
    { key: 'project', label: 'Proje', width: '140px' },
    { key: 'protocolNumber', label: 'Protokol', width: '130px' },
    { key: 'indication', label: 'Endikasyon', width: '130px' },
    { key: 'site', label: 'Merkez', width: '220px' },
    { key: 'pi', label: 'PI', width: '180px' },
    { key: 'delegation', label: 'Delegasyon', width: '110px' },
    { key: 'targetedPatients', label: 'Hedeflenen Hasta', width: '90px' },
    { key: 'screenedPatients', label: 'Screen Edilen', width: '90px' },
    { key: 'screenFailure', label: 'Screen Failure', width: '90px' },
    { key: 'randomization', label: 'Randomizasyon', width: '90px' },
    { key: 'currentPatients', label: 'Güncel Hasta', width: '90px' },
    { key: 'sponsor', label: 'Sponsor', width: '150px' }
  ];

  return (
    <div className={`${styles.scTrackingSection} ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Header - collapsible */}
      <div
        className={styles.scTrackingHeader}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={styles.scTrackingTitle}>
          <FaTable className={styles.scTrackingIcon} />
          <h2>SC Takip Tablosu</h2>
          {dataLoaded && (
            <span className={styles.scBadge}>
              {summaryStats.uniqueSCs} SC · {summaryStats.uniqueProjects} Proje
            </span>
          )}
        </div>
        <div className={styles.scTrackingToggle}>
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </div>

      {isExpanded && (
        <div className={styles.scTrackingContent}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <FaSpinner className={styles.spinner} />
              <span>Veriler yükleniyor...</span>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className={styles.summaryCards}>
                <div className={styles.summaryCard}>
                  <FaUsers className={styles.summaryIcon} style={{ color: '#3182ce' }} />
                  <div>
                    <span className={styles.summaryValue}>{summaryStats.uniqueSCs}</span>
                    <span className={styles.summaryLabel}>SC Personel</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <FaProjectDiagram className={styles.summaryIcon} style={{ color: '#805ad5' }} />
                  <div>
                    <span className={styles.summaryValue}>{summaryStats.uniqueProjects}</span>
                    <span className={styles.summaryLabel}>Proje</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <FaHospital className={styles.summaryIcon} style={{ color: '#38a169' }} />
                  <div>
                    <span className={styles.summaryValue}>{summaryStats.uniqueSites}</span>
                    <span className={styles.summaryLabel}>Merkez</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <FaUserMd className={styles.summaryIcon} style={{ color: '#d69e2e' }} />
                  <div>
                    <span className={styles.summaryValue}>{summaryStats.totalActive}</span>
                    <span className={styles.summaryLabel}>Aktif Hasta</span>
                  </div>
                </div>
              </div>

              {/* Toolbar */}
              <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="SC, Proje, Merkez, PI ara..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className={styles.searchInput}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <FaFilter className={styles.filterIcon} />
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="all">Tüm Roller</option>
                    <option value="sc">Saha Koordinatörü</option>
                    <option value="backup">Backup Personeli</option>
                    <option value="nurse">Hemşire</option>
                    <option value="scSorumlusu">SC Sorumlusu</option>
                  </select>
                </div>
                <div className={styles.filterGroup}>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="active">Aktif</option>
                    <option value="inactive">Pasif</option>
                  </select>
                </div>
                <button 
                  className={styles.refreshButton} 
                  onClick={handleRefresh}
                  disabled={isLoading}
                  title="Verileri Yenile"
                >
                  <FaSync className={isLoading ? styles.spinning : ''} />
                  <span>Yenile</span>
                </button>
                <button 
                  className={styles.clearButton} 
                  onClick={handleClearFilters}
                  title="Filtreleri Temizle"
                >
                  <FaTimes />
                  <span>Temizle</span>
                </button>
                <button className={styles.excelButton} onClick={handleExcelExport}>
                  <FaFileExcel />
                  <span>Excel İndir</span>
                </button>
              </div>

              {/* Table */}
              <div className={styles.tableWrapper}>
                <table className={styles.scTable}>
                  <thead>
                    <tr>
                      {columns.map(col => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          style={{ minWidth: col.width }}
                          className={styles.sortableHeader}
                        >
                          <div className={styles.thContent}>
                            <span>{col.label}</span>
                            <span className={styles.sortIcon}>{renderSortIcon(col.key)}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedRows.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} className={styles.emptyState}>
                          <FaTable />
                          <span>Kayıt bulunamadı</span>
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedRows.map((row, index) => (
                        <tr
                          key={`${row.employeeId}_${row.projectId}_${row.centerId}_${index}`}
                          className={`${index % 2 === 0 ? styles.evenRow : ''} ${
                            row.role === 'Backup Personeli' ? styles.backupRow : ''
                          } ${row.employeeStatus === 'inactive' ? styles.inactiveRow : ''}`}
                        >
                          <td className={styles.nameCell}>
                            <div className={styles.nameAvatar}>
                              {row.scName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </div>
                            <span>{row.scName}</span>
                          </td>
                          <td>
                            <span className={`${styles.statusBadge} ${
                              row.employeeStatus === 'active' ? styles.statusActive : styles.statusInactive
                            }`}>
                              {row.employeeStatus === 'active' ? 'Aktif' : 'Pasif'}
                            </span>
                          </td>
                          <td>{row.phone}</td>
                          <td className={styles.emailCell}>{row.email}</td>
                          <td>
                            <span className={`${styles.fteBadge} ${
                              row.serviceFte === 'Backup' ? styles.fteBackup :
                              row.serviceFte === '1' ? styles.fteFull :
                              styles.ftePartial
                            }`}>
                              {row.serviceFte || '-'}
                            </span>
                          </td>
                          <td className={styles.projectCell}>{row.project}</td>
                          <td>{row.protocolNumber}</td>
                          <td>
                            {row.indication && (
                              <span className={styles.indicationBadge}>{row.indication}</span>
                            )}
                          </td>
                          <td className={styles.siteCell} title={row.site}>
                            {row.site}
                            {row.isArchived && (
                              <span className={styles.archivedBadge}>
                                {getArchivedAssignmentLabel(row)}
                              </span>
                            )}
                          </td>
                          <td className={styles.piCell} title={row.pi}>{row.pi}</td>
                          <td>{row.delegation}</td>
                          <td className={styles.numericCell}>{row.targetedPatients || '-'}</td>
                          <td className={styles.numericCell}>
                            <span className={styles.screenedValue}>{row.screenedPatients || '-'}</span>
                          </td>
                          <td className={styles.numericCell}>
                            <span className={styles.failureValue}>{row.screenFailure || '-'}</span>
                          </td>
                          <td className={styles.numericCell}>
                            <span className={styles.randomValue}>{row.randomization || '-'}</span>
                          </td>
                          <td className={styles.numericCell}>
                            <span className={styles.activeValue}>{row.currentPatients || '-'}</span>
                          </td>
                          <td className={styles.sponsorCell}>{row.sponsor}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer info */}
              <div className={styles.tableFooter}>
                <span className={styles.rowCount}>
                  Toplam {filteredAndSortedRows.length} kayıt gösteriliyor
                  {searchTerm && ` (${tableRows.length} kayıttan filtrelendi)`}
                  {(roleFilter !== 'all' || statusFilter !== 'all') && (
                    <span className={styles.filterStatus}>
                      {' • '}
                      {roleFilter !== 'all' && `Rol: ${roleFilter}`}
                      {roleFilter !== 'all' && statusFilter !== 'all' && ', '}
                      {statusFilter !== 'all' && `Durum: ${statusFilter}`}
                    </span>
                  )}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SCTrackingTable;
